import React, { useEffect, useRef } from 'react';
import {
  Badge,
  Banner,
  Button,
  Form,
  Gutter,
  Icon,
  Layout,
  LayoutItem,
  ParagraphHeader,
  Textarea,
  Well,
} from '@axiom/ui';

import { CandidateAiSearchSchema } from '../../schema/candidate-ai-search-schema';
import { useAiSearch } from '../../providers/AiSearchContextProvider';

export const CandidateAiSearch = () => {
  const { search, isLoading, searchText } = useAiSearch();
  // Button shows a loader while its onClick Promise is pending. We hold that
  // Promise across renders via resolveRef: handleClick stashes the resolver,
  // the first effect below resolves it once isLoading flips false, and the
  // second effect re-attaches a pending Promise on remount if a search is
  // already in-flight (e.g. user navigated away and back during the ~2min run).
  const resolveRef = useRef<(() => void) | null>(null);
  const fireSubmitRef = useRef<
    ((e?: React.SyntheticEvent) => void | Promise<void>) | null
  >(null);
  const submitCountRef = useRef(0);

  const handleClick = async () => {
    if (isLoading) {
      return new Promise<void>(resolve => {
        resolveRef.current = resolve;
      });
    }

    const submitCountBefore = submitCountRef.current;
    await fireSubmitRef.current?.();
    // Formik's handleSubmit fires submitForm asynchronously and discards its
    // Promise, so `await fireSubmit()` returns before validation + onSubmit
    // have run. Yield one task tick so onSubmit (which increments
    // submitCountRef) has a chance to fire before we read it.
    await new Promise(resolve => {
      setTimeout(resolve, 0);
    });

    if (submitCountRef.current === submitCountBefore) {
      return;
    }

    return new Promise<void>(resolve => {
      resolveRef.current = resolve;
    });
  };

  useEffect(() => {
    if (!isLoading && resolveRef.current) {
      resolveRef.current();
      resolveRef.current = null;
    }
  }, [isLoading]);

  useEffect(() => {
    if (isLoading && !resolveRef.current) {
      void handleClick();
    }
  }, []);

  return (
    <Gutter top="24px">
      <Form
        name="AI_SEARCH_FORM"
        schema={CandidateAiSearchSchema}
        initialValues={{ aiSearchText: searchText }}
        onSubmit={(_changeData, _actions, formData) => {
          submitCountRef.current++;
          search(formData.aiSearchText);
        }}
      >
        {({ fireSubmit }) => {
          fireSubmitRef.current = fireSubmit;

          return (
            <>
              <Well name="AI_SEARCH" background="blue">
                <Badge background="yellow">Beta</Badge>
                <Gutter bottom="16px" />
                <Textarea
                  label={
                    <ParagraphHeader as="span">
                      <Layout position="middle left" inline>
                        <LayoutItem rightGutter="8px">
                          <Icon name="intelligence" />
                        </LayoutItem>
                        <LayoutItem>
                          Describe the talent you're looking for
                        </LayoutItem>
                      </Layout>
                    </ParagraphHeader>
                  }
                  name="aiSearchText"
                  description="Tell us what you need in a sentence or two. You can also paste an email, job description or transcript. Reservoir and Alum talent will not be returned."
                  formatting="modern"
                />
                <Gutter bottom="16px" />
                <Layout position="right">
                  <LayoutItem>
                    <Button
                      pattern="secondary"
                      name="AI_SEARCH_BUTTON"
                      onClick={handleClick}
                    >
                      SEARCH
                    </Button>
                  </LayoutItem>
                </Layout>
              </Well>
              <Gutter bottom="24px" />
              <Banner impact="high">
                Don't use this tab while the search is running (~2 minutes). You
                can continue using AHQ in another tab. Starting a new search or
                closing this browser tab will cancel the search.
              </Banner>
            </>
          );
        }}
      </Form>
    </Gutter>
  );
};
